/** Lightweight rich text (contenteditable) — mobile-friendly 44px toolbar targets */

const ALLOWED_TAGS = new Set([
    'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'H2', 'H3', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'IMG', 'A',
]);

export function sanitizeStoryHtml(html) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walk = (node) => {
        [...node.childNodes].forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const tag = child.tagName;
                if (!ALLOWED_TAGS.has(tag)) {
                    const text = doc.createTextNode(child.textContent || '');
                    child.replaceWith(text);
                    return;
                }
                if (tag === 'A') {
                    const href = child.getAttribute('href') || '';
                    if (!/^https?:\/\//i.test(href)) child.removeAttribute('href');
                    else {
                        child.setAttribute('rel', 'noopener noreferrer');
                        child.setAttribute('target', '_blank');
                    }
                }
                if (tag === 'IMG') {
                    const src = child.getAttribute('src') || '';
                    if (!/^https?:\/\//i.test(src) && !src.startsWith('data:image/')) {
                        child.remove();
                        return;
                    }
                    child.setAttribute('alt', child.getAttribute('alt') || 'Campaign image');
                }
                [...child.attributes].forEach((attr) => {
                    if (!['href', 'src', 'alt', 'target', 'rel'].includes(attr.name)) {
                        child.removeAttribute(attr.name);
                    }
                });
                walk(child);
            }
        });
    };
    walk(doc.body);
    return doc.body.innerHTML.trim();
}

export function renderRichEditorHtml(initialHtml = '') {
    return `
        <div class="fg-fund-rich" data-rich-editor>
            <div class="fg-fund-rich__toolbar" role="toolbar" aria-label="Formatting">
                <button type="button" class="fg-fund-rich__btn" data-cmd="bold" title="Bold"><b>B</b></button>
                <button type="button" class="fg-fund-rich__btn" data-cmd="italic" title="Italic"><i>I</i></button>
                <button type="button" class="fg-fund-rich__btn" data-cmd="formatBlock" data-value="h2" title="Heading">H2</button>
                <button type="button" class="fg-fund-rich__btn" data-cmd="formatBlock" data-value="h3" title="Subheading">H3</button>
                <button type="button" class="fg-fund-rich__btn" data-cmd="insertUnorderedList" title="Bullet list">•</button>
                <button type="button" class="fg-fund-rich__btn" data-cmd="insertOrderedList" title="Numbered list">1.</button>
                <button type="button" class="fg-fund-rich__btn" data-cmd="formatBlock" data-value="blockquote" title="Quote">❝</button>
                <button type="button" class="fg-fund-rich__btn" id="rich-insert-image" title="Insert image">🖼</button>
            </div>
            <div id="p-full-desc" class="fg-fund-rich__area" contenteditable="true" role="textbox" aria-multiline="true">${initialHtml}</div>
            <input type="file" id="rich-image-file" accept="image/*" hidden>
        </div>`;
}

export function attachRichEditor(root) {
    const wrap = root?.querySelector('[data-rich-editor]');
    if (!wrap) return () => '';

    const area = wrap.querySelector('#p-full-desc');
    const fileInput = wrap.querySelector('#rich-image-file');

    wrap.querySelectorAll('.fg-fund-rich__btn[data-cmd]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            area?.focus();
            const cmd = btn.dataset.cmd;
            const val = btn.dataset.value || null;
            document.execCommand(cmd, false, val);
        });
    });

    wrap.querySelector('#rich-insert-image')?.addEventListener('click', () => fileInput?.click());
    fileInput?.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        const url = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsDataURL(file);
        });
        area?.focus();
        document.execCommand('insertImage', false, url);
        fileInput.value = '';
    });

    return () => sanitizeStoryHtml(area?.innerHTML || '');
}
